/* eslint-disable react/prop-types */
import React, { useState } from 'react';
// @mui
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TablePagination,
  Button,
  Stack,
  Tooltip,
} from '@mui/material';
import { Box } from '@mui/system';
import { VisibilityOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// components
import Scrollbar from '../../../components/scrollbar';
import ConfirmationModal from '../../../components/confirmationModal/ConfirmationModal';

// sections
import ClientReqHead from '../../../components/clientHeadTable/ClientHeadTable';

// local components
import StatusBadge from './components/StatusBadge';
import ListingTypeBadge from './components/ListingTypeBadge';
import RequestDetailDrawer from './components/RequestDetailDrawer';

// ----------------------------------------------------------------------

export default function ClientReqTable({ requests, handleChangePage, page, onDownload, onApprove, onReject, onUpdateSoldStatus, tabValue }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [rowsPerPage] = useState(8);
  const navigate = useNavigate();

  // Confirmation modal state
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [actionType, setActionType] = useState('');

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRow, setDrawerRow] = useState(null);

  const TABLE_HEAD = [
    { id: 'categoryName', label: t('reversationRequest.category'), alignRight: isRtl && true },
    { id: 'listingType', label: t('reversationRequest.listingType'), alignRight: isRtl && true },
    { id: 'requestType', label: t('reversationRequest.type'), alignRight: isRtl && true },
    { id: 'flagNameEn', label: t('reversationRequest.unit_piece_name'), alignRight: isRtl && true },
    // { id: 'buyerName', label: t('reversationRequest.customer'), alignRight: isRtl && true },
    { id: 'phoneNumber', label: t('reversationRequest.phone'), alignRight: isRtl && true },
    { id: 'saleType', label: t('reversationRequest.saleType'), alignRight: isRtl && true },
    { id: 'requestStatus', label: t('reversationRequest.request_status'), alignRight: isRtl && true },
    { id: 'creationDate', label: t('reversationRequest.creation_date'), alignRight: isRtl && true },
    { id: 'expirationDate', label: t('reversationRequest.expiration_date'), alignRight: isRtl && true },
    ...(tabValue === 1 ? [
      { id: 'rentYears', label: t('reversationRequest.rentYears'), alignRight: isRtl && true },
      { id: 'rentStartDate', label: t('reversationRequest.rentStartDate'), alignRight: isRtl && true },
    ] : []),
    { id: 'view', label: isRtl ? 'عرض' : 'View', alignRight: isRtl && true },
  ];

  const handleOpenDrawer = (row) => {
    setDrawerRow(row);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setDrawerRow(null);
  };

  // --- Drawer action handlers (open confirmation modal from drawer) ---
  const handleDrawerApprove = () => {
    setSelectedId(drawerRow.id);
    setActionType('approve');
    setOpen(true);
  };

  const handleDrawerReject = () => {
    setSelectedId(drawerRow.id);
    setActionType('reject');
    setOpen(true);
  };

  const handleDrawerMarkSold = () => {
    setSelectedId(drawerRow.id);
    setSelectedRow(drawerRow);
    setActionType('markSold');
    setOpen(true);
  };

  const handleDrawerMarkNotSold = () => {
    setSelectedId(drawerRow.id);
    setSelectedRow(drawerRow);
    setActionType('markNotSold');
    setOpen(true);
  };

  return (
    <>
      <Scrollbar>
        <TableContainer sx={{ minWidth: 1000 }}>
          <Table sx={{ direction: isRtl ? 'rtl' : 'ltr' }}>
            <ClientReqHead style={{ whiteSpace: 'nowrap' }} headLabel={TABLE_HEAD} centerHeaders />
            <TableBody>
              {requests?.content?.map((row, index) => {
                const {
                  userPhone,
                  requestType,
                  fullNameAr,
                  fullNameEn,
                  id,
                  invoiceId,
                  creationDate,
                  expireDate,
                  requestStatus,
                  flagNameEn,
                  flagNameAr,
                  transactionCompleted,
                  categoryNameEn,
                  categoryNameAr,
                  listingType,
                  rentYears,
                  rentStartDate,
                  rentEndDate,
                  flagDetails,
                  categoryDetails
                } = row;

                const currentListingType = listingType || 'SALE';

                return (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={row.id}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* Category */}
                    <TableCell sx={{ textAlign: isRtl ? 'right' : 'left', maxWidth: 130 }} component="th" scope="row">
                      <Tooltip title={isRtl ? categoryNameAr || '' : categoryNameEn || ''} placement="top" arrow enterDelay={400}>
                        <Typography
                          sx={{ color: 'text.secondary', fontWeight: '400', fontSize: '12px', lineHeight: '20px' }}
                          variant="subtitle2"
                          noWrap
                        >
                          {isRtl ? categoryNameAr || t('General.noData') : categoryNameEn || t('General.noData')}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Listing Type Badge */}
                    <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }} component="th" scope="row">
                      <ListingTypeBadge listingType={currentListingType} />
                    </TableCell>

                    {/* Request Type */}
                    <TableCell sx={{ textAlign: isRtl ? 'center' : 'center' }} component="th" scope="row">
                      <Typography
                        sx={{ color: 'text.secondary', fontWeight: '400', fontSize: '12px', lineHeight: '20px' }}
                        variant="subtitle2"
                        noWrap
                      >
                        {' '}
                        {/* {t(`General.${requestType}`)} */}
                        {t(`${flagDetails?.type}`)}

                      </Typography>
                    </TableCell>

                    {/* Unit / Piece Name */}
                    <TableCell sx={{ textAlign: isRtl ? 'right' : 'left', maxWidth: 150 }} component="th" scope="row">
                      <Tooltip title={isRtl ? flagNameAr || '' : flagNameEn || ''} placement="top" arrow enterDelay={400}>
                        <Typography
                          sx={{ color: 'text.secondary', fontWeight: '400', fontSize: '12px', lineHeight: '20px' }}
                          variant="subtitle2"
                          noWrap
                        >
                          {isRtl ? flagNameAr || t('General.noData') : flagNameEn || t('General.noData')}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Customer */}
                    {/* <TableCell sx={{ textAlign: isRtl ? 'right' : 'left', maxWidth: 140 }} component="th" scope="row">
                      <Tooltip title={isRtl ? fullNameAr || '' : fullNameEn || ''} placement="top" arrow enterDelay={400}>
                        <Typography
                          sx={{ color: 'text.secondary', fontWeight: '400', fontSize: '12px', lineHeight: '20px' }}
                          variant="subtitle2"
                          noWrap
                        >
                          {isRtl ? fullNameAr || t('General.noData') : fullNameEn || t('General.noData')}
                        </Typography>
                      </Tooltip>
                    </TableCell> */}

                    {/* Phone */}
                    <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }} component="th" scope="row">
                      <Typography
                        sx={{ color: 'text.secondary', fontWeight: '400', fontSize: '12px', lineHeight: '20px' }}
                        variant="subtitle2"
                        noWrap
                      >
                        {' '}
                        {userPhone || t('General.noData')}
                      </Typography>
                    </TableCell>

                    {/* Sale Type */}
                    <TableCell sx={{ textAlign: isRtl ? 'center' : 'center' }} component="th" scope="row">
                      {categoryDetails?.saleType === 'READY' || categoryDetails?.saleType === 'OFF_PLAN' ? (
                        <Typography
                          noWrap
                          sx={{
                            display: 'inline-block',
                            px: 2.3,
                            py: 0.6,
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            backgroundColor: categoryDetails.saleType === 'READY' ? '#F4F6F8' : '#FFF4E5',
                            color: categoryDetails.saleType === 'READY' ? '#637381' : '#B76E00',
                          }}
                        >
                          {categoryDetails.saleType === 'READY'
                            ? t('reversationRequest.saleType_ready')
                            : t('reversationRequest.saleType_offPlan')}
                        </Typography>
                      ) : (
                        <Typography sx={{ color: 'text.disabled', fontSize: '12px' }}>—</Typography>
                      )}
                    </TableCell>

                    {/* Request Status Badge */}
                    <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }} component="th" scope="row">
                      <StatusBadge requestStatus={requestStatus} tabValue={tabValue} minWidth="150px" />
                    </TableCell>

                    {/* Creation Date */}
                    <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }} component="th" scope="row">
                      <Typography
                        sx={{ color: 'text.secondary', fontWeight: '400', fontSize: '12px', lineHeight: '20px' }}
                        variant="subtitle2"
                        noWrap
                      >
                        {' '}
                        {creationDate || ''}
                      </Typography>
                    </TableCell>

                    {/* Expiration Date */}
                    <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }} component="th" scope="row">
                      <Typography
                        sx={{ color: 'text.secondary', fontWeight: '400', fontSize: '12px', lineHeight: '20px' }}
                        variant="subtitle2"
                        noWrap
                      >
                        {' '}
                        {expireDate || ''}
                      </Typography>
                    </TableCell>

                    {/* Rent-specific columns */}
                    {tabValue === 1 && (
                      <>
                        <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }} component="th" scope="row">
                          <Typography sx={{ color: 'text.secondary', fontWeight: '400', fontSize: '12px', lineHeight: '20px' }} variant="subtitle2" noWrap>
                            {rentYears ? `${rentYears} ${t('reversationRequest.years')}` : t('General.noData')}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: isRtl ? 'right' : 'left' }} component="th" scope="row">
                          <Typography sx={{ color: 'text.secondary', fontWeight: '400', fontSize: '12px', lineHeight: '20px' }} variant="subtitle2" noWrap>
                            {rentStartDate ? rentStartDate.split(' ')[0] : t('General.noData')}
                          </Typography>
                        </TableCell>
                      </>
                    )}

                    {/* ★ View Button — single column replacing both action columns */}
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        // startIcon={<VisibilityOutlined sx={{ fontSize: '16px !important' }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDrawer(row);
                        }}
                        sx={{
                          borderColor: '#32de84',
                          color: '#0D2416',
                          borderRadius: '8px',
                          fontWeight: 600,
                          fontSize: '12px',
                          textTransform: 'none',
                          px: 2,
                          py: 0.6,
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            backgroundColor: '#E7F7EF',
                            borderColor: '#0CAF60',
                          },
                        }}
                      >
                        {isRtl ? 'عرض' : 'View'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>

      {requests?.totalElements === 0 && (
        <Box textAlign="center">
          <h5>{t('General.noDataFound')}</h5>
        </Box>
      )}

      {requests?.totalPages > 0 && (
        <TablePagination
          rowsPerPageOptions={[8]}
          component="div"
          count={requests.totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
        />
      )}

      {/* ── Detail Drawer ── */}
      <RequestDetailDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        row={drawerRow}
        tabValue={tabValue}
        onApproveClick={handleDrawerApprove}
        onRejectClick={handleDrawerReject}
        onMarkSoldClick={handleDrawerMarkSold}
        onMarkNotSoldClick={handleDrawerMarkNotSold}
      />

      {/* ── Confirmation Modal ── */}
      {open && (
        <div>
          <ConfirmationModal
            open={open}
            handelClose={() => {
              setOpen(false);
            }}
            title={
              actionType === 'approve'
                ? isRtl
                  ? 'تأكيد الموافقة على الحجز'
                  : 'Approve Reservation Request'
                : actionType === 'reject'
                  ? isRtl
                    ? 'تأكيد إلغاء الحجز'
                    : 'Confirm Reservation Cancellation'
                  : actionType === 'markSold'
                    ? isRtl
                      ? (tabValue === 1 ? 'تأكيد الإيجار' : 'تأكيد البيع')
                      : (tabValue === 1 ? 'Mark as Rented' : 'Mark as Sold')
                    : isRtl
                      ? (tabValue === 1 ? 'تأكيد إلغاء الإيجار' : 'تأكيد إلغاء البيع')
                      : (tabValue === 1 ? 'Mark as Not Rented' : 'Mark as Not Sold')
            }
            message={
              actionType === 'approve'
                ? isRtl
                  ? 'هل أنت متأكد من رغبتك في الموافقة على هذا الحجز؟'
                  : 'Are you sure you want to approve this reservation request?'
                : actionType === 'reject'
                  ? isRtl
                    ? 'هل أنت متأكد من رغبتك في إلغاء هذا الحجز؟'
                    : 'Are you sure you want to reject this reservation request?'
                  : actionType === 'markSold'
                    ? isRtl
                      ? (tabValue === 1 ? 'هل أنت متأكد من رغبتك في تعيين هذه الوحدة كمؤجرة؟' : 'هل أنت متأكد من رغبتك في تعيين هذه الوحدة كمباعة؟')
                      : (tabValue === 1 ? 'Are you sure you want to mark this unit as rented?' : 'Are you sure you want to mark this unit as sold?')
                    : isRtl
                      ? (tabValue === 1 ? 'هل أنت متأكد من رغبتك في إلغاء حالة الإيجار لهذه الوحدة؟' : 'هل أنت متأكد من رغبتك في إلغاء حالة البيع لهذه الوحدة؟')
                      : (tabValue === 1 ? 'Are you sure you want to mark this unit as not rented?' : 'Are you sure you want to mark this unit as not sold?')
            }
            onUpdate={() => {
              setOpen(false);
              handleCloseDrawer();
              if (actionType === 'approve') {
                onApprove(selectedId);
              } else if (actionType === 'reject') {
                onReject(selectedId);
              } else if (actionType === 'markSold') {
                onUpdateSoldStatus({
                  id: selectedId,
                  status: '1',
                });
              } else if (actionType === 'markNotSold') {
                onUpdateSoldStatus({
                  id: selectedId,
                  status: '0',
                });
              }
            }}
          />
        </div>
      )}
    </>
  );
}
